const GOOGLE_MAPS_API_KEY = String(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();

let googleMapsLoadPromise = null;

function hasKey() {
  return Boolean(GOOGLE_MAPS_API_KEY);
}

function loadGoogleMapsApi() {
  if (!hasKey()) return Promise.resolve(null);
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (googleMapsLoadPromise) return googleMapsLoadPromise;

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps-loader="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google?.maps || null), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps script")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      GOOGLE_MAPS_API_KEY
    )}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";
    script.onload = () => resolve(window.google?.maps || null);
    script.onerror = () => reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
}

function geocodeByPlaceId(maps, placeId) {
  return new Promise((resolve) => {
    const geocoder = new maps.Geocoder();
    geocoder.geocode({ placeId }, (results, status) => {
      if (status !== "OK" || !Array.isArray(results) || !results.length) {
        resolve(null);
        return;
      }
      const first = results[0];
      resolve({
        label: String(first?.formatted_address || "").trim(),
        lat: String(first?.geometry?.location?.lat?.() ?? "").trim(),
        long: String(first?.geometry?.location?.lng?.() ?? "").trim(),
      });
    });
  });
}

export async function searchGoogleAddresses(query) {
  const q = String(query || "").trim();
  if (!q || q.length < 3 || !hasKey()) return [];
  const maps = await loadGoogleMapsApi();
  if (!maps?.places) return [];

  const autocomplete = new maps.places.AutocompleteService();
  const predictions = await new Promise((resolve) => {
    autocomplete.getPlacePredictions({ input: q, types: ["geocode"] }, (items) => {
      resolve(Array.isArray(items) ? items : []);
    });
  });

  const detailed = await Promise.all(
    predictions.slice(0, 7).map((item) => geocodeByPlaceId(maps, item.place_id))
  );
  const out = [];
  const seen = new Set();
  for (const row of detailed) {
    if (!row || !row.label || !row.lat || !row.long) continue;
    const key = `${row.lat}|${row.long}|${row.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export async function reverseGeocodeGoogle({ lat, long }) {
  const safeLat = String(lat || "").trim();
  const safeLong = String(long || "").trim();
  if (!safeLat || !safeLong || !hasKey()) return "";
  const maps = await loadGoogleMapsApi();
  if (!maps?.Geocoder) return "";

  return await new Promise((resolve) => {
    const geocoder = new maps.Geocoder();
    geocoder.geocode(
      { location: { lat: Number(safeLat), lng: Number(safeLong) } },
      (results, status) => {
        if (status !== "OK" || !Array.isArray(results) || !results.length) {
          resolve("");
          return;
        }
        resolve(String(results[0]?.formatted_address || "").trim());
      }
    );
  });
}

